import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import PaymentsOSErrorLabel from './PaymentsOSErrorLabel';
import { useTranslation } from 'react-i18next';

const PaymentsOSCardForm = React.forwardRef(({ config, onFormElementError }, ref) => {

    const [cardNumber, setCardNumber] = React.useState(null);
    const { t } = useTranslation();
    const [value, setValue] = React.useState('');
    const [cardHolderNameDirty, setCardHolderNameDirty] = React.useState(false);
    const [submitTried, setSubmitTried] = React.useState(false);
    const [errorCardNumber, setErrorCardNumber] = React.useState(undefined);
    const [errorExpiryDate, setErrorExpiryDate] = React.useState(undefined);
    const [errorCvv, setErrorCvv] = React.useState(undefined);

    React.useImperativeHandle(ref, () => ({
        tokenize() {
            const additionalData = {
                holder_name: document.getElementById('cardholder-name').value // This field is mandatory
            };
            return window.POS.createToken(cardNumber, {
                additionalData,
                environment: config.production ? 'live' : 'test' // the PaymentsOS environment you're connecting to
            });
        },
        clickedSubmit() {
            setSubmitTried(true);
        }
    }));

    const create = useCallback(() => {
        const fonts = [{src: 'https://fonts.googleapis.com/css?family=Source+Code+Pro', }];

        const formElements = new window.POS.Fields(config.public_key, {fonts});

        const placeholders = {
            cardNumber: '1234 1234 1234 1234',
            expDate: 'MM / YY',
            cvv: '123'
        };

        const style = {
            base: {
                backgroundColor: '#FFFFFF',
                fontSize: '18px',
                padding: '6px',
                textAlign: 'start',
            },
        };

        const classes = {
            base: 'baseClass'
        };

        // Instantiate the fields to show and mount them to the DOM.
        const cardNumberFormElement = formElements.create('cardNumber', {
            classes,
            placeholders,
            style
        });
        cardNumberFormElement.mount('#card-number');
        cardNumberFormElement.on('change', (event) => {
            if (event.error === undefined) {
                setErrorCardNumber(undefined);
                onFormElementError({card: undefined});
            } else {
                setErrorCardNumber(event.error?.pan);
                onFormElementError({card: event.error?.pan});
            }

        });
        setCardNumber(cardNumberFormElement);

        const expiryFormElement = formElements.create('creditCardExpiry', {
            classes,
            placeholders,
            style
        });
        expiryFormElement.mount('#exp-date');
        expiryFormElement.on('change', (event) => {
            if (event.error === undefined) {
                setErrorExpiryDate(undefined);
                onFormElementError({expiry: undefined});
            } else {
                setErrorExpiryDate(event.error?.expiry);
                onFormElementError({expiry: event.error?.expiry});
            }
        });

        const cvvFormElement = formElements.create('cvv', {
            classes,
            placeholders,
            style
        });
        cvvFormElement.mount('#cvv');
        cvvFormElement.on('change', (event) => {
            if (event.error === undefined) {
                setErrorCvv(undefined);
                onFormElementError({cvv: undefined});
            } else {
                setErrorCvv(event.error?.cvv);
                onFormElementError({cvv: event.error?.cvv});
            }
        });
    }, [config.public_key, onFormElementError]);

    useEffect(() => {
        const script = window.document.createElement('script');
        script.src = 'https://js.paymentsos.com/v3/latest/secure-fields.min.js';
        script.onload = create;
        window.document.body.appendChild(script);
        return () => window.document.body.removeChild(script);
    }, [create]);

    return (
        <>
            <div className="wz-lyriapay-formblock">
                <label>{t('cardHolderName')}</label>
                <input
                    id="cardholder-name"
                    placeholder="John Doe"
                    value={value}
                    onChange={e => {
                        setCardHolderNameDirty(true);
                        setValue(e.target.value);
                    }}/>
                <PaymentsOSErrorLabel
                    content={
                        (cardHolderNameDirty || submitTried) && value === '' ? t('error_required') : null
                    }
                />
            </div>
            <div >
                <label>{t('cardNumberLabel')}</label>
                <div id="card-number" className="wz-lyriapay-paymentsos-form-div"></div>
                <PaymentsOSErrorLabel
                    content={
                        errorCardNumber ? t('cardNumberInvalid') : null
                    }
                />
            </div>
            <div >
                <label>{t('expiryLabel')}</label>
                <div id="exp-date" className="wz-lyriapay-paymentsos-form-div"></div>
                <PaymentsOSErrorLabel
                    content={
                        errorExpiryDate ? t('expiryInvalid') : null
                    }
                />
            </div>
            <div >
                <label>{t('cvcLabel')}</label>
                <div id="cvv" className="wz-lyriapay-paymentsos-form-div"></div>
                <PaymentsOSErrorLabel
                    content={
                        errorCvv ? t('cvcInvalid') : null
                    }
                />
            </div>
        </>
    );
});

PaymentsOSCardForm.propTypes = {
    config: PropTypes.object,
    onFormElementError: PropTypes.func
};

export default PaymentsOSCardForm;